import { useEffect, useRef, useState } from "react";

import type { Prospect, ProspectStage } from "../api/hooks";
import { prospectStages, useMoveProspectStage, useProspect } from "../api/hooks";
import { SlideOver } from "../components/SlideOver";
import { hasNextAction, nextActionLabel } from "../lib/prospect";
import { dateLabel, sinceLabel } from "../lib/relativeTime";

import { TouchComposer } from "./-TouchComposer";

import styles from "./-ProspectPanel.module.scss";

// A dot colour per channel, so a timeline can be read as a shape - three greys
// in a row is nobody replying, and a green is a meeting.
const channelClasses: Record<string, string> = {
  email: styles.dotBlue,
  call: styles.dotPurple,
  meeting: styles.dotGreen,
  note: styles.dotQuiet,
};

const channelLabels: Record<string, string> = {
  email: "email",
  call: "call",
  meeting: "meeting",
  note: "note",
};

export function ProspectPanel({
  prospect,
  stageLabels,
  onClose,
}: {
  // The row that was clicked, so the panel has a name and a stage to draw
  // before the detail arrives. Opening onto an empty panel for a round trip is
  // the difference between a panel and a page.
  prospect: Prospect;
  stageLabels: Record<ProspectStage, string>;
  onClose: () => void;
}) {
  const { data, isPending, isError } = useProspect(prospect.id);
  const move = useMoveProspectStage();

  const [moving, setMoving] = useState(false);
  const [logging, setLogging] = useState(false);
  const firstStage = useRef<HTMLButtonElement>(null);
  const moveButton = useRef<HTMLButtonElement>(null);
  const logButton = useRef<HTMLButtonElement>(null);
  // Which of the two branches just closed, so focus goes back to the control
  // that opened it rather than always to Move stage.
  const closedComposer = useRef(false);
  const [dismissedReason, setDismissedReason] = useState("");
  const [message, setMessage] = useState("");

  // The detail once it lands, the row until then: the two agree about
  // everything the header draws, and the row is already on screen.
  const current = data?.prospect ?? prospect;

  // The composer's write counts too: the dialog is this component's, and a
  // close mid-write unmounts the composer and takes its callbacks with it.
  const [composerBusy, setComposerBusy] = useState(false);
  const busy = move.isPending || composerBusy;

  // Revealing the picker unmounts the button that was pressed, so focus falls
  // to <body> - inside a dialog claiming aria-modal, with nothing announced and
  // the next Tab landing back at the top of the panel. It follows the control
  // that replaced it, and returns when the picker closes.
  //
  // busy is a dependency and not decoration: a browser blurs a focused element
  // when it is disabled, which is what the picker's buttons do while a move is
  // in flight, so focus has to come back when they are enabled again. Doing it
  // from the mutation's onError instead does nothing at all - that runs inside
  // the same batch, before the re-render that re-enables the button, and
  // focus() on a disabled button is a no-op.
  //
  // Only when focus has actually left, though. The reason field stays enabled
  // while a move is in flight, so an operator can be typing into it when the
  // request comes back - and taking focus off them mid-sentence to put it on a
  // button is worse than the state being recovered from.
  useEffect(() => {
    if (busy) return;

    // Nothing has focus, which is where a disabled button leaves it.
    const stranded = document.activeElement === document.body;

    if (moving) {
      if (stranded) firstStage.current?.focus();
      return;
    }

    // The composer focuses its own first control on open; on close the button
    // that opened it is back, and focus has to come with it.
    if (logging) return;

    if (closedComposer.current) {
      closedComposer.current = false;
      logButton.current?.focus();
      return;
    }

    moveButton.current?.focus();
  }, [moving, logging, busy]);

  function moveTo(stage: ProspectStage) {
    setMessage("");

    if (stage === "dismissed" && dismissedReason.trim() === "") {
      // Said here rather than sent and refused: the API enforces it too, but a
      // round trip to be told to type a sentence is a round trip.
      setMessage("Say why this one is being dismissed.");
      return;
    }

    move.mutate(
      { id: prospect.id, stage, dismissedReason },
      {
        onSuccess: () => {
          setMoving(false);
          setDismissedReason("");
        },
        onError: () => setMessage("That could not be moved. Try again."),
      },
    );
  }

  const facts = [current.organisationKind, current.organisationCounty]
    .filter((part) => part !== "")
    .join(" · ");

  return (
    <SlideOver title={current.organisationName} onClose={onClose} busy={busy}>
      {facts !== "" && <div className={styles.facts}>{facts}</div>}

      <div className={styles.chips}>
        <span className={styles.chipStage}>{stageLabels[current.stage]}</span>
        {current.fitScore !== null && (
          <span className={styles.chipFit}>{current.fitScore} of 5 fit</span>
        )}
        {/* A date rather than a relative phrase: when a prospect entered the
            pipeline is a fact to compare against, not news. */}
        <span className={styles.chipQuiet}>
          Found {dateLabel(current.discoveredAt)}
        </span>
      </div>

      {current.dismissedReason !== "" && (
        <div className={styles.dismissed}>
          Dismissed: {current.dismissedReason}
        </div>
      )}

      {current.rationale !== "" && (
        <>
          <div className={styles.sectionLabel}>Why this one</div>
          <div className={styles.rationale}>{current.rationale}</div>
        </>
      )}

      <div className={styles.sectionLabel}>Outreach history</div>

      {isPending && (
        <div className={styles.state} role="status">
          Loading the history…
        </div>
      )}

      {isError && (
        <div className={styles.state} role="alert">
          The history could not be loaded.
        </div>
      )}

      {data?.interactions.length === 0 && (
        <div className={styles.state} role="status">
          Nothing logged yet.
        </div>
      )}

      {data !== undefined && data.interactions.length > 0 && (
        <ol className={styles.timeline}>
          {data.interactions.map((interaction) => (
            <li key={interaction.id} className={styles.entry}>
              <span
                className={`${styles.dot} ${
                  channelClasses[interaction.channel] ?? styles.dotQuiet
                }`}
                aria-hidden="true"
              />
              <div>
                <div className={styles.entrySummary}>{interaction.summary}</div>
                {interaction.note !== "" && (
                  <div className={styles.entryNote}>{interaction.note}</div>
                )}
                <div className={styles.entryMeta}>
                  {sinceLabel(interaction.occurredAt)} ·{" "}
                  {channelLabels[interaction.channel] ?? interaction.channel}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      {/* Said only when there is more, so a short history does not carry a
          note about what it is not showing. */}
      {data !== undefined && data.interactionsTotal > data.interactions.length && (
        <div className={styles.state}>
          Showing {data.interactions.length} of {data.interactionsTotal}
        </div>
      )}

      <div className={styles.next}>
        <span className={styles.sectionLabel}>Next action</span>
        {/* The same words the row behind uses, from the same function: written
            twice they had already drifted, and a panel that contradicts the row
            it was opened from is worse than either wording alone. */}
        <span
          className={
            current.overdue
              ? styles.nextOverdue
              : hasNextAction(current)
                ? styles.nextValue
                : styles.nextNone
          }
        >
          {nextActionLabel(current)}
        </span>
      </div>

      {message !== "" && (
        <div className={styles.error} role="alert">
          {message}
        </div>
      )}

      {logging && (
        <TouchComposer
          prospect={current}
          stageLabels={stageLabels}
          onClose={() => {
            closedComposer.current = true;
            setLogging(false);
          }}
          onBusyChange={setComposerBusy}
        />
      )}

      <div className={styles.actions}>
        {!moving && !logging && (
          <button
            type="button"
            ref={logButton}
            className={styles.primary}
            disabled={busy}
            onClick={() => setLogging(true)}
          >
            Log a touch
          </button>
        )}

        {!moving && !logging && (
          <button
            type="button"
            ref={moveButton}
            className={styles.action}
            disabled={busy}
            onClick={() => setMoving(true)}
          >
            Move stage
          </button>
        )}

        {moving && (
          <div className={styles.stagePicker} role="group" aria-label="Move to stage">
            {prospectStages
              .filter((stage) => stage !== current.stage)
              .map((stage, index) => (
                <button
                  key={stage}
                  type="button"
                  ref={index === 0 ? firstStage : undefined}
                  className={styles.stageChoice}
                  disabled={busy}
                  onClick={() => moveTo(stage)}
                >
                  {stageLabels[stage]}
                </button>
              ))}

            {/* Asked for beside the stage rather than after it: the reason and
                the move are one decision, and the API refuses a dismissal
                without one. */}
            <input
              type="text"
              className={styles.reason}
              aria-label="Why this one is being dismissed"
              placeholder="Why, if dismissing"
              value={dismissedReason}
              onChange={(event) => setDismissedReason(event.target.value)}
            />

            <button
              type="button"
              className={styles.action}
              disabled={busy}
              onClick={() => {
                setMoving(false);
                setMessage("");
              }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </SlideOver>
  );
}
